<template>
    <div>
        <template v-if="pageLoading">
            <loading-spinner/>
        </template>

        <template v-else>
            <x-container fluid app-width>
                <x-layout align-end mb-4>
                    <x-flex xs8>
                        <h1 class="section__header__title">{{ welcomeMessage }}</h1>
                    </x-flex>

                    <x-flex v-if="notInsurer" xs4 text-xs-right pb-1>
                        <router-link to="/agent/risk/new">
                            <x-btn large color="primary" class="ma-0">
                                Get quotes

                                <x-icon class="ml-2">trending_flat</x-icon>
                            </x-btn>
                        </router-link>
                    </x-flex>
                </x-layout>

                <x-layout>
                    <x-flex xs2 class="sidebar">
                        <risk-filter
                            v-for="(option, index) in riskTypeOptions"
                            :key="index"
                            :option="option"
                            :active="riskFilters.includes(option.value)"
                            v-model="riskFilters"
                            :stats="stats"
                        />

                        <status-filters
                            :options="statusFilterOptions"
                            v-model="statusFilters"
                        />
                    </x-flex>

                    <x-flex xs10 class="content">
                        <x-card>
                            <x-layout justify-between>
                                <x-flex>
                                    <x-layout class="stat-cards">
                                        <x-flex v-for="(statFilter, index) in statFilters" :key="index" class="grow-0">
                                            <stat-card
                                                :filter="statFilter"
                                                :active-filter="activeStatFilter"
                                                :stats="stats"
                                                @toggleStatFilter="toggleStatFilter"
                                            />
                                        </x-flex>
                                    </x-layout>
                                </x-flex>

                                <x-flex text-xs-right>
                                    <x-text-field
                                        class="search-field d-inline-flex"
                                        v-model="searchTerm"
                                        placeholder="Search"
                                        :append-icon="searchTerm !== null && searchTerm.length === 0 ? 'search' : null"
                                        hide-details
                                    />
                                </x-flex>
                            </x-layout>

                            <x-layout>
                                <x-flex xs12>
                                    <x-data-table
                                        class="fit-to-width no-row-hover"
                                        :headers="tableHeaders"
                                        :items="submissions"
                                        :pagination.sync="pagination"
                                        :disable-initial-sort="true"
                                        :custom-sort="sortRisks"
                                        hide-actions
                                    >
                                        <template slot="no-data">
                                            <p class="error-message text-xs-center" v-if="!tableLoading && !submissions.length">{{ noResultsMessage }}</p>
                                            <loading-spinner data-test="loading" v-else class="compact"/>
                                        </template>

                                        <template slot="items" slot-scope="props">
                                            <tr @click="openSubmission(props.item)" class="item-row" :class="props.item.status">
                                                <slot name="tableRow" :row="props" :actions="getRowActions(props.item.id, props.item.type)" :searchTerm="tableSearchTerm" />
                                            </tr>
                                        </template>
                                    </x-data-table>
                                </x-flex>
                            </x-layout>

                            <x-layout mt-5 mb-5>
                                <x-flex xs12 text-xs-center>
                                    <x-pagination v-if="pagination.totalItems" circle v-model="currentPage" :length="pagination.totalPages" :total-visible="15" @input="reloadSubmissions""/>
                                </x-flex>
                            </x-layout>
                        </x-card>
                    </x-flex>
                </x-layout>
            </x-container>
        </template>
    </div>
</template>

<script>
    import _ from 'lodash';
    import CheckIcon from '~/components/Shared/UIComponents/CheckIcon';
    import RiskFilter from '~/components/Shared/UIComponents/RiskFilter';
    import StatusFilters from '~/components/Shared/UIComponents/StatusFilters';
    import StatCard from '~/components/Shared/UIComponents/StatCard';

    export default {
        name: 'Dashboard',
        components: {
            CheckIcon,
            RiskFilter,
            StatusFilters,
            StatCard
        },
        props: {
            statusFilterOptions: Array,
            riskTypeOptions: Array,
            tableHeaders: Array,
            endpoint: String,
            submissionRoute: String,
            manualSubmissionRoute: String,
            statFilters: {
                type: Array,
                default: () => []
            },
            info: Object,
            role: String
        },
        data() {
            return {
                pageLoading: true,
                tableLoading: true,
                submissions: [],
                submissionActions: [],
                statusFilterMenuOpen: false,
                activeStatFilter: false,
                riskFilterMenuOpen: false,
                activeRiskFilter: false,
                searchTerm: '',
                tableSearchTerm: '',
                statusFilters: [],
                appliedStatusFilters: [],
                riskFilters: [],
                appliedRiskFilters: [],
                sortBy: {
                    column: 'created_at',
                    order: 'desc'
                },
                pagination: {
                    page: 1,
                    totalItems: null,
                    totalPages: null,
                    rowsPerPage: null,
                    sortBy: 'created_at',
                    descending: true,
                },
                currentPage: null,
                stats: [],
            };
        },
        computed: {
            welcomeMessage() {
                return this.$root.user.name ? `Welcome, ${this.$root.user.name.split(' ')[0]}` : 'Welcome';
            },
            noResultsMessage() {
                const searchText = this.searchTerm !== null && this.searchTerm.length > 0 ? ` matching the search term "${this.searchTerm}"` : '';

                return `Sorry, there are no results${searchText}.`;
            },
            pages() {
                if (this.pagination.rowsPerPage === null || this.pagination.totalItems === null) { return 0; }

                const totalPages = Math.ceil(this.pagination.totalItems / this.pagination.rowsPerPage);

                if (this.pagination.page > totalPages) {
                    this.pagination.page = 1;
                }

                return totalPages;
            },
            notInsurer() {
                return this.role && this.role !== 'insurer';
            },
        },
        watch: {
            searchTerm: _.debounce(function (newVal) {
                if (newVal === null) {
                    this.searchTerm = "";
                }

                if (newVal) {
                    this.activeStatFilter = false;
                }

                this.reloadSubmissions();

                this.tableSearchTerm = this.searchTerm;
            }, 300),
            tableSearchTerm(newVal) {
                if (newVal === null) {
                    this.searchTerm = '';
                    this.tableSearchTerm = '';
                }
            },
            appliedStatusFilters(newVal) {
                this.statusFilters = newVal;

                if (newVal.length > 0) {
                    this.activeStatFilter = false;
                }

                this.reloadSubmissions();
                this.saveDashboardFilters();
            },
            appliedRiskFilters(newVal) {
                this.riskFilters = newVal;

                if (newVal.length > 0) {
                    this.activeRiskFilter = false;
                }

                this.reloadSubmissions();
                this.saveDashboardFilters();
            },
            riskFilters(newVal) {
                this.appliedRiskFilters = newVal;
            },
            statusFilters(newVal) {
                this.appliedStatusFilters = newVal;

                this.allStatusesSelected = this.appliedStatusFilters.length === this.statusFilterOptions.length;
            },
        },
        async mounted() {
            this.$eventBus.$on('submissionsChanged', () => {
                this.reloadSubmissions();
            });
            if (sessionStorage.getItem('tx-dashboard-filters')) {
                const dashboardFilters = JSON.parse(sessionStorage.getItem('tx-dashboard-filters'));
                this.appliedStatusFilters = dashboardFilters.statusFilters || [];
                this.appliedRiskFilters = dashboardFilters.riskFilters || [];
            }
            this.reloadSubmissions();
        },
        methods: {
            async reloadSubmissions(page) {
                this.tableLoading = true;
                this.submissions = [];
                let params = [];

                params.push('page=' + (page !== undefined ? page : 1));

                if (this.appliedStatusFilters.length) {
                    this.appliedStatusFilters.forEach((filter) => params.push(`filters[]=${filter}`));
                }

                if (this.appliedRiskFilters.length) {
                    this.appliedRiskFilters.forEach((filter) => params.push(`riskFilters[]=${filter}`));
                }

                if (this.activeStatFilter) {
                    params.push('statFilter=' + this.activeStatFilter.searchKey);
                }

                if (this.searchTerm !== null && this.searchTerm.length > 0) {
                    params.push('search=' + this.searchTerm);
                }

                if (this.sortBy.column !== null) {
                    params.push('sort_by=' + this.sortBy.column);
                    params.push('order=' + this.sortBy.order);
                }

                const endpoint = this.endpoint + (params.length ? '?' + params.join('&') : '');

                await axios.get(this.$root.api(endpoint), { responseType: "json" })
                    .then((res) => {
                        this.submissions = res.data.data.map(sub => {
                            if (sub.type === 'manual_submission') {
                                sub.id = 'M' + sub.id;
                            }
                            return sub;
                        });

                        this.stats = res.data.stats;
                        this.pagination.rowsPerPage = res.data.per_page;
                        this.pagination.totalItems = res.data.total;
                        this.pagination.totalPages = res.data.last_page;
                        this.pagination.page = res.data.current_page;
                        this.currentPage = res.data.current_page;
                        this.submissionActions = res.data.actions;

                        this.tableLoading = false;
                        this.pageLoading = false;
                    });
            },
            openSubmission(submission) {
                const isManualSubmission = submission.type === 'manual_submission';
                this.$router.push(
                    {
                        name: isManualSubmission ? this.manualSubmissionRoute : this.submissionRoute,
                        params: {
                            id: isManualSubmission ? submission.id.substring(1) : submission.id,
                        }
                    }
                );
            },
            limitOfLiability(contractValue, indemnity) {
                return (indemnity / 100) * contractValue;
            },
            closeStatFilter() {
                this.activeStatFilter = false;
                this.reloadSubmissions();
            },
            toggleStatFilter(stat) {
                if (this.activeStatFilter.key === stat.key) {
                    this.closeStatFilter();
                    return false;
                }

                this.activeStatFilter = stat;
                this.searchTerm = '';
                this.appliedStatusFilters = [];
                this.reloadSubmissions();
            },
            getRowActions(riskId, riskType) {
                return this.submissionActions.filter(action => {
                    //we don't have actions on dashboard for manual submission tracker
                    return action.risk_id === riskId && !['manual_submission'].includes(riskType)
                }).reduce((actionsObject, action) => {
                    actionsObject[action.type] = action;
                    return actionsObject;
                }, {});
            },
            isMultiObligor(risk) {
                return risk.obligors.length > 1 || risk.guarantors.length > 1;
            },
            searchObligor(obligors, searchRegex) {
                const filteredObligors = obligors.filter(obligor => {
                    return obligor.name.search(searchRegex) !== -1 || obligor.country.name.search(searchRegex) !== -1
                })

                return filteredObligors.length > 0;
            },
            sortRisks(items, index, isDesc) {
                const order = isDesc ? 'desc' : 'asc';
                if (this.sortBy.column === index && this.sortBy.order === order) {
                    return items;
                }
                this.sortBy.column = index;
                this.sortBy.order = isDesc ? 'desc' : 'asc';
                this.reloadSubmissions();

                return items;
            },
            saveDashboardFilters() {
                sessionStorage.setItem('tx-dashboard-filters', JSON.stringify({
                    statusFilters: this.appliedStatusFilters,
                    riskFilters: this.appliedRiskFilters,
                }));
            },
        }
    }
</script>

<style lang="scss" scoped>
    .stat-card-text-button-container {
        width: 100%;
        margin-top: 15px;
    }

    .section__header__title {
        font-size: rem(28px);
        font-weight: 400;
        font-stretch: normal;
        font-style: normal;
        line-height: 1em;
        text-align: left;
    }

    .sidebar {
        min-width: 260px;

        .x-card {
            background: transparent !important;
        }
    }

    .x-card {
        @include card-gradient;
        padding: 16px;

        &:before {
            border-width: 1px;
        }
    }

    .stat-cards {
        .flex {
            padding: 0 6px;

            &:first-child {
                padding-left: 12px;
            }
        }
    }

    .search-field {
        width: 325px;

        :deep(.x-input__control .x-input__slot .x-text-field__slot) {
            height: 52px !important;

            input {
                height: 100%;
                max-height: 52px;
            }
        }

        &.x-input.x-text-field.x-text-field--outline :deep(.x-input__control .x-input__slot input::placeholder) {
            color: $color-grey-7;
            font-weight: 600;
        }
    }

    .status-field {
        width: 200px;
    }

    :deep(.x-table) {
        margin-top: 36px;
        position: relative;

        &.theme--dark {
            tbody {
                tr {
                    cursor: pointer;
                    box-shadow: inset 0 0 0 1px transparent;
                    transition: box-shadow $transition-length;
                    height: 36px;

                    td {
                        white-space: nowrap;

                        .text-scroller {
                            overflow: hidden;
                            display: block;
                            &.ellipsis {
                                text-overflow: ellipsis;
                            }
                        }
                    }
                }
            }
        }
    }
</style>
